'use client';
import { create } from 'zustand';
import { SummaryDetails } from '../types/summary-details';
import { Series } from '../types/series';
import { LessonProgresses } from '../types/lessons';
import { CustomerSubscription } from '../types/customer';

type DataType<T> = {
  status: {
    isLoading: boolean;
    isError: boolean;
  };
  data: T | null;
  error: Error | null;
};

type FavouriteSeries = {
  isLoading: boolean;
  isFavourite: boolean;
  documentId: string | null;
};

type SeriesByIdState = {
  series: DataType<Series>;
  summaryDetails: DataType<SummaryDetails>;
  lessonProgress: DataType<LessonProgresses>['status'];
  customerSubscription: DataType<CustomerSubscription>;
  favouriteSeries: FavouriteSeries | null;
  setCustomerSubscription: (data: Partial<SeriesByIdState['customerSubscription']>) => void;
  setFavouriteSeries: (data: Partial<FavouriteSeries>) => void;
  setSeries: (data: Partial<SeriesByIdState['series']>) => void;
  setLessonProgress: (data: Partial<SeriesByIdState['lessonProgress']>) => void;
  setSummaryDetails: (data: Partial<SeriesByIdState['summaryDetails']>) => void;
  updateLessonProgress: (data: LessonProgresses) => void;
  createLessonProgress: (data: LessonProgresses & { lessonDocumentId: string | null }) => void;
  resetStore: () => void;
};

const initialState: Omit<
  SeriesByIdState,
  | 'setSeries'
  | 'setSummaryDetails'
  | 'setCustomerSubscription'
  | 'createLessonProgress'
  | 'updateLessonProgress'
  | 'setLessonProgress'
  | 'setFavouriteSeries'
  | 'resetStore'
> = {
  summaryDetails: {
    status: { isLoading: false, isError: false },
    data: null,
    error: null,
  },
  customerSubscription: {
    status: { isLoading: false, isError: false },
    data: null,
    error: null,
  },
  lessonProgress: { isLoading: false, isError: false },
  series: {
    status: { isLoading: false, isError: false },
    data: null,
    error: null,
  },
  favouriteSeries: null,
};

export const useSeriesByIdStore = create<SeriesByIdState>((set) => ({
  ...initialState,
  setCustomerSubscription: (data) =>
    set((state) => ({
      customerSubscription: {
        ...state.customerSubscription,
        ...data,
      },
    })),
  setSummaryDetails: (data) =>
    set((state) => ({
      summaryDetails: {
        ...state.summaryDetails,
        ...data,
      },
    })),
  setLessonProgress: (data) =>
    set((state) => ({
      lessonProgress: {
        ...state.lessonProgress,
        ...data,
      },
    })),
  setSeries: (data) =>
    set((state) => ({
      series: {
        ...state.series,
        ...data,
      },
    })),
  setFavouriteSeries: (data) =>
    set((state) => ({
      favouriteSeries: state.favouriteSeries
        ? { ...state.favouriteSeries, ...data }
        : { isLoading: false, isFavourite: false, documentId: null, ...data },
    })),
  createLessonProgress: (data) =>
    set((state): any => {
      return {
        series: {
          ...state.series,
          data: state?.series?.data?.map((series) => ({
            ...series,
            lessons: series?.lessons?.map((lesson) => {
              if (lesson.documentId === data.lessonDocumentId) {
                return {
                  ...lesson,
                  lessonProgresses: [data],
                };
              }
              return lesson;
            }),
          })),
        },
      };
    }),
  updateLessonProgress: (data: LessonProgresses) =>
    set((state): any => {
      return {
        series: {
          ...state.series,
          data: state?.series?.data?.map((series) => ({
            ...series,
            lessons: series?.lessons?.map((lesson) => {
              if (lesson?.lessonProgresses?.some((lp) => lp.documentId === data.documentId)) {
                return {
                  ...lesson,
                  lessonProgresses: lesson.lessonProgresses.map((lessonProgress) =>
                    lessonProgress.documentId === data.documentId ? data : lessonProgress,
                  ),
                };
              }
              return lesson;
            }),
          })),
        },
      };
    }),
  resetStore: () =>
    set(() => ({
      ...initialState,
    })),
}));
